import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router';
import type Transition from '@ember/routing/transition';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import type SessionContextService from '../../../services/session-context';
import type { FinancialInstitution as FinancialInstitutionModel } from 'tio-data/data/resources/financial-institution.type';
import type BetaExperienceService from 'tio-employee/services/beta-experience';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type ObservabilityUploadRouteModel = Resolved<
  ReturnType<AuthenticatedObservabilityUploadRoute['model']>
>;

export type UploadQueryParams = {
  source?: string;
  providers?: string;
  returnTo?: string;
};

/** An entry that intends to upload a document (wizard/legacy form) rather
 *  than a direct visit (Document Center). */
export const hasUploadIntent = ({ source, providers }: UploadQueryParams): boolean => {
  return !!(source || providers);
};

/** An entry from the reimagined UI, which always gets the reimagined wizard
 *  regardless of the beta preference so in-reimagined navigation stays coherent. */
export const isReimaginedEntry = ({ source, returnTo }: UploadQueryParams): boolean => {
  return !!(source?.startsWith('reimagined') || returnTo?.startsWith('authenticated.reimagined'));
};

export default class AuthenticatedObservabilityUploadRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare remoteEventLogger: RemoteEventLoggerService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;

  async model(_params: object, transition: Transition) {
    const person = this.sessionContext.user.person;
    const queryParams = (transition.to?.queryParams ?? {}) as UploadQueryParams;
    const showsReimagined = this.betaExperience.isEnabled || isReimaginedEntry(queryParams);

    // The financial institution list only feeds the legacy form's
    // servicer-instructions drawer; skip the fetch on reimagined paths.
    let financialInstitutions: FinancialInstitutionModel[] = [];
    if (!showsReimagined) {
      const { content: fiContent } = (await this.store.request(
        query<FinancialInstitutionModel>('financial-institution', {
          'filter[isActive]': true,
          'filter[hasObservabilityInstructions]': true,
        })
      )) as { content: { data: FinancialInstitutionModel[] } };

      financialInstitutions = [...fiContent.data].sort(
        (a: FinancialInstitutionModel, b: FinancialInstitutionModel) =>
          a.name?.localeCompare(b.name)
      );
    }

    this.remoteEventLogger.logObservabilityEvent({
      eventName: 'INITIALIZE',
      component: 'AuthenticatedObservabilityUploadRoute',
    });

    return { person, financialInstitutions };
  }
}
